<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    tabindex="-1"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ type === 'CREATE'? $t('Create Customer'): $t('Edit Customer')  }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>
    <div
      class="offcanvas-body p-0"
      v-else
    >
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-circle-info me-2" />
        {{ $t('Customer Information') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-md-6">
            <label
              for="fullname"
              class="form-label"
            >
              {{ $t('Full Name') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm ">
              <input
                id="fullname"
                type="text"
                v-model.trim="v$.form.fullname.$model"
                class="form-control"
                :class="v$.form.fullname.$error || (errors?.fullname?.length && oldParams.fullname == form.fullname)? 'is-invalid': ''"
                :placeholder="$t('Enter full name') + '...'"
                :disabled="viewOnly"
              >
              <div v-for="error of v$.form.fullname.$errors"
                class="invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <template v-if="oldParams.fullname === form.fullname">
                <div v-for="error of errors.fullname"
                  class="invalid-feedback"
                >
                  {{ $t(error) }}
                </div>
              </template>
            </div>
          </div>
          <div class="col-md-6">
            <label
              for="national_id"
              class="form-label"
            >
              {{ $t('National ID') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm ">
              <input
                id="national_id"
                type="text"
                inputmode="numeric"
                maxlength="13"
                v-model.trim="v$.form.national_id.$model"
                class="form-control"
                :class="v$.form.national_id.$error || (errors?.national_id?.length && oldParams.national_id == form.national_id)? 'is-invalid': ''"
                :placeholder="$t('13-digit ID card number') + '...'"
                :disabled="viewOnly"
                @keypress="onlyDigits($event)"
              >
              <div v-for="error of v$.form.national_id.$errors"
                class="invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <template v-if="oldParams.national_id === form.national_id">
                <div v-for="error of errors.national_id"
                  class="invalid-feedback"
                >
                  {{ $t(error) }}
                </div>
              </template>
            </div>
          </div>
          <div class="col-md-3">
            <label
              class="form-label"
            >
              {{ $t('Customer Group') }}
              <span class="text-danger">*</span>
            </label>
            <select2
              v-model="v$.form.customer_group_id.$model"
              :class="v$.form.customer_group_id.$error || (errors?.customer_group_id?.length && oldParams.customer_group_id == form.customer_group_id)? 'is-invalid': ''"
              :options="select2CustomerGroup.options"
              :settings="select2CustomerGroup.setting"
              :disabled="viewOnly"
            />
            <div v-for="error of v$.form.customer_group_id.$errors"
              class="invalid-feedback d-block"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
            <template v-if="oldParams.customer_group_id === form.customer_group_id">
              <div v-for="error of errors.customer_group_id"
                class="invalid-feedback d-block"
              >
                {{ $t(error) }}
              </div>
            </template>
          </div>
          <div class="col-md-3">
            <label
              class="form-label"
            >
              {{ $t('Branch') }}
            </label>
            <select2
              v-model="form.branch_id"
              :class="errors?.branch_id?.length && oldParams.branch_id == form.branch_id? 'is-invalid': ''"
              :options="select2Branch.options"
              :settings="select2Branch.setting"
              :disabled="viewOnly"
            />
            <template v-if="oldParams.branch_id === form.branch_id">
              <div v-for="error of errors.branch_id"
                class="invalid-feedback d-block"
              >
                {{ $t(error) }}
              </div>
            </template>
          </div>
          <div class="col-md-6">
            <label
              for="email"
              class="form-label"
            >
              {{ $t('Email') }}
            </label>
            <div class="input-group-sm ">
              <input
                id="email"
                type="email"
                v-model.trim="v$.form.email.$model"
                class="form-control"
                :class="v$.form.email.$error || (errors?.email?.length && oldParams.email == form.email)? 'is-invalid': ''"
                :placeholder="$t('Enter email') + '...'"
                :disabled="viewOnly"
              >
              <div v-for="error of v$.form.email.$errors"
                class="invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <template v-if="oldParams.email === form.email">
                <div v-for="error of errors.email"
                  class="invalid-feedback"
                >
                  {{ $t(error) }}
                </div>
              </template>
            </div>
          </div>
          <div class="col-md-3">
            <label
              for="line_id"
              class="form-label"
            >
              {{ $t('Line ID') }}
            </label>
            <div class="input-group-sm ">
              <input
                id="line_id"
                type="text"
                v-model.trim="v$.form.line_id.$model"
                class="form-control"
                :class="v$.form.line_id.$error || (errors?.line_id?.length && oldParams.line_id == form.line_id)? 'is-invalid': ''"
                :placeholder="$t('Enter line id') + '...'"
                :disabled="viewOnly"
              >
              <div v-for="error of v$.form.line_id.$errors"
                class="invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <template v-if="oldParams.line_id === form.line_id">
                <div v-for="error of errors.line_id"
                  class="invalid-feedback"
                >
                  {{ $t(error) }}
                </div>
              </template>
            </div>
          </div>
          <div class="col-md-3">
            <label
              for="telephone1"
              class="form-label"
            >
              {{ $t('Telephone') + 1 }}
            </label>
            <div class="input-group-sm ">
              <input
                id="telephone1"
                type="tel"
                v-model.trim="v$.form.telephone1.$model"
                class="form-control"
                :class="v$.form.telephone1.$error || (errors?.telephone1?.length && oldParams.telephone1 == form.telephone1)? 'is-invalid': ''"
                :placeholder="$t('Enter telephone') + '...'"
                :disabled="viewOnly"
              >
              <div v-for="error of v$.form.telephone1.$errors"
                class="invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <template v-if="oldParams.telephone1 === form.telephone1">
                <div v-for="error of errors.telephone1"
                  class="invalid-feedback"
                >
                  {{ $t(error) }}
                </div>
              </template>
            </div>
          </div>
          <div class="col-md-3">
            <label
              for="telephone2"
              class="form-label"
            >
              {{ $t('Telephone') + 2 }}
            </label>
            <div class="input-group-sm ">
              <input
                id="telephone2"
                type="tel"
                v-model.trim="v$.form.telephone2.$model"
                class="form-control"
                :class="v$.form.telephone2.$error || (errors?.telephone2?.length && oldParams.telephone2 == form.telephone2)? 'is-invalid': ''"
                :placeholder="$t('Enter telephone') + '...'"
                :disabled="viewOnly"
              >
              <div v-for="error of v$.form.telephone2.$errors"
                class="invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <template v-if="oldParams.telephone2 === form.telephone2">
                <div v-for="error of errors.telephone2"
                  class="invalid-feedback"
                >
                  {{ $t(error) }}
                </div>
              </template>
            </div>
          </div>
          <div
            v-if="type === 'EDIT' && form.member_code"
            class="col-md-3"
          >
            <label
              for="member_code"
              class="form-label"
            >
              {{ $t('Member Code') }}
            </label>
            <div class="input-group-sm">
              <input
                id="member_code"
                type="text"
                :value="form.member_code"
                class="form-control"
                readonly
              >
            </div>
          </div>
          <div class="col-md-12">
            <div class="form-check">
              <input
                id="marketing_consent"
                type="checkbox"
                class="form-check-input"
                v-model="form.marketing_consent"
                :disabled="viewOnly"
              >
              <label
                for="marketing_consent"
                class="form-check-label"
              >
                {{ $t('Consent to receive marketing communications') }}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-house-flag me-2" />
        {{ $t('Customer Address') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-md-12">
            <label
              for="address"
              class="form-label"
            >
              {{ $t('Address') }}
            </label>
            <div class="input-group-sm ">
              <input
                id="address"
                type="text"
                v-model.trim="v$.form.address.$model"
                class="form-control"
                :class="v$.form.address.$error || (errors?.address?.length && oldParams.address == form.address)? 'is-invalid': ''"
                :placeholder="$t('Enter address') + '...'"
                :disabled="viewOnly"
              >
              <div v-for="error of v$.form.address.$errors"
                class="invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <template v-if="oldParams.address === form.address">
                <div v-for="error of errors.address"
                  class="invalid-feedback"
                >
                  {{ $t(error) }}
                </div>
              </template>
            </div>
          </div>
          <div class="col-md-3" >
            <label for="postal_code" class="form-label">
              {{ $t('Zipcode') }}
            </label>
            <div class="input-group-sm ">
              <input
                id="postal_code"
                type="text"
                v-model.trim="v$.form.postal_code.$model"
                class="form-control"
                :class="v$.form.postal_code.$error || (errors?.postal_code?.length && oldParams.postal_code == form.postal_code)? 'is-invalid': ''"
                :placeholder="$t('Enter zipcode') + '...'"
                :disabled="viewOnly"
              >
              <div v-for="error of v$.form.postal_code.$errors"
                class="invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <template v-if="oldParams.postal_code === form.postal_code">
                <div v-for="error of errors.postal_code"
                  class="invalid-feedback"
                >
                  {{ $t(error) }}
                </div>
              </template>
            </div>
          </div>
          <div class="col-md-3">
            <label
              class="form-label"
            >
              {{ $t('Sub District') }}
            </label>
            <select2
              v-model="v$.form.sub_district.$model"
              :class="errors?.sub_district?.length && oldParams.sub_district == form.sub_district? 'is-invalid': ''"
              :options="districts"
              :settings="select2FormAddress.setting"
              :disabled="viewOnly"
              @select="selectAddresses"
            />
            <template v-if="oldParams.sub_district === form.sub_district">
              <div v-for="error of errors.sub_district"
                class="invalid-feedback d-block"
              >
                {{ $t(error) }}
              </div>
            </template>
          </div>
          <div class="col-md-3">
            <label
              class="form-label"
            >
              {{ $t('District') }}
            </label>
            <select2
              v-model="v$.form.district.$model"
              :class="errors?.district?.length && oldParams.district == form.district? 'is-invalid': ''"
              :options="amphoes"
              :settings="select2FormAddress.setting"
              :disabled="viewOnly"
              @select="selectAddresses"
            />
            <template v-if="oldParams.district === form.district">
              <div v-for="error of errors.district"
                class="invalid-feedback d-block"
              >
                {{ $t(error) }}
              </div>
            </template>
          </div>
          <div class="col-md-3">
            <label
              class="form-label"
            >
              {{ $t('Province') }}
            </label>
            <select2
              v-model="v$.form.province.$model"
              :class="errors?.province?.length && oldParams.province == form.province? 'is-invalid': ''"
              :options="provinces"
              :settings="select2FormAddress.setting"
              :disabled="viewOnly"
              @select="selectAddresses"
            />
            <template v-if="oldParams.province === form.province">
              <div v-for="error of errors.province"
                class="invalid-feedback d-block"
              >
                {{ $t(error) }}
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading"
    >
      <ladda
        text="Save"
        v-if="type === 'CREATE'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleCreate"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleUpdate"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { Customers } from '@/services/endpoint/customers.service';
import Select2 from 'vue3-select2-component';
import { searchAddressByZipcode } from 'thai-address-database';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation
  },
  props: {
    type: {
      type: String,
      default: 'CREATE'
    },
    customerId: {
      type: Number,
      default: 0
    },
    viewOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      loadingSave: false,
      hasPermission: false,
      select2CustomerGroup: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
        }
      },
      select2Branch: {
        options: [],
        setting: {
          allowClear: false,
        }
      },
      select2FormAddress: {
        setting: {
          minimumResultsForSearch: Infinity,
        }
      },
      form: {
        fullname: '',
        national_id: '',
        customer_group_id: '',
        branch_id: '',
        email: '',
        telephone1: '',
        telephone2: '',
        line_id: '',
        address: '',
        postal_code: '',
        sub_district: '0',
        district: '0',
        province: '0',
        member_code: '',
        marketing_consent: false
      },
      errors: {
        fullname: [],
        national_id: [],
        customer_group_id: [],
        branch_id: [],
        email: [],
        telephone1: [],
        telephone2: [],
        line_id: [],
        address: [],
        postal_code: [],
        sub_district: [],
        district: [],
        province: []
      },
      oldParams: {
        fullname: '',
        national_id: '',
        customer_group_id: '',
        branch_id: '',
        email: '',
        telephone1: '',
        telephone2: '',
        line_id: '',
        address: '',
        postal_code: '',
        sub_district: '0',
        district: '0',
        province: '0'
      },
    }
  },
  components: {
    PlaceholderContent,
    AccessDenied,
    Ladda,
    Select2
  },
  methods: {
    selectAddresses({ id, text, module }) {
      if (module == 'sub_district') {
        if (id != '0') {
          let address = this.addresses.find((addresse) => addresse.district == id);
          this.form.district = address.amphoe;
          this.form.province = address.province;
          this.form.postal_code = address.zipcode;
        }
      }

      if (module == 'district') {
        this.form.sub_district = '0';
        if (id != '0') {
          let address = this.addresses.find((addresse) => addresse.amphoe == id);
          this.form.province = address.province;
          this.form.postal_code = address.zipcode;
        }
      }

      if (module == 'province') {
        this.form.sub_district = '0';
        this.form.district = '0';
        if (id != '0') {
          let address = this.addresses.find((addresse) => addresse.province == id);
          this.form.postal_code = address.zipcode;
        }
      }
    },
    setDropdownOptions(customerGroups, branches) {
      const vm = this;
      this.select2CustomerGroup.options = customerGroups.map(function (item) {
        return {
          id: item.id,
          text: item.name
        }
      });
      this.select2Branch.options = [
        { id: '', text: vm.$t('Select') + '...' }
      ].concat(branches.map(function (item) {
        return {
          id: item.id,
          text: item.name
        }
      }));
    },
    async getCreateData() {
      await Customers.getCreate().then(res => {
        this.setDropdownOptions(res.data.data.customer_groups, res.data.data.branches);
        if (res.data.data.customer_groups.length) {
          this.form.customer_group_id = res.data.data.customer_groups[0].id;
        }
        this.hasPermission = true;
        this.loading = false;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
        throw err;
      });
    },
    async getUpdateData() {
      await Customers.getUpdate(this.customerId).then(res => {
        // Support both response shapes: nested ({ data: { data, filter } })
        // and sibling ({ data: {...}, filter: {...} }) like other modules.
        const payload = res.data.data;
        const data = payload?.data ?? payload;
        const filter = res.data.filter ?? payload?.filter ?? {};

        // Set select2 options first so the bound value resolves to a label.
        this.setDropdownOptions(filter.customer_groups || [], filter.branches || []);
        this.form.customer_group_id = data.customer_group_id || '';
        this.form.branch_id = data.branch_id || '';

        this.form.fullname = data.fullname || '';
        this.form.national_id = data.national_id || '';
        this.form.email = data.email || '';
        this.form.telephone1 = data.telephone1 || '';
        this.form.telephone2 = data.telephone2 || '';
        this.form.line_id = data.line_id || '';
        this.form.member_code = data.member_code || '';
        this.form.marketing_consent = !!data.marketing_consent;
        this.form.address = data.address || '';
        this.form.postal_code = data.postal_code || '';
        setTimeout(() => {
          this.form.sub_district = data.sub_district || '0';
          this.form.district = data.district || '0';
          this.form.province = data.province || '0';
        }, 1);
        this.hasPermission = true;
        this.loading = false;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
        throw err;
      });
    },
    onlyDigits(e) {
      // เลขบัตรเป็นตัวเลขล้วน — กันพิมพ์ตัวอักษร/อักขระอื่น
      if (!/[0-9]/.test(e.key)) {
        e.preventDefault();
      }
    },
    setParamsBeforeSend() {
      let params = _.clone(this.form);

      // member_code is server-generated and read-only — never send it back.
      delete params.member_code;
      // PDPA consent is a real boolean; serialise to 1/0 so the falsy-trim below
      // doesn't drop an explicit "false".
      params.marketing_consent = params.marketing_consent ? 1 : 0;

      // Address fields are an all-or-nothing group: drop the whole group
      // unless every part is filled in.
      if (
        !params.address ||
        !params.postal_code ||
        params.sub_district == '0' || !params.sub_district ||
        params.district == '0' || !params.district ||
        params.province == '0' || !params.province
      ) {
        delete params.address;
        delete params.postal_code;
        delete params.sub_district;
        delete params.district;
        delete params.province;
      }

      return $.each(params, (key, value) => {
        if (value === "" || value === null || value.length === 0) {
          delete params[key];
        }
      });
    },
    // ล้าง error รอบก่อนก่อนยิงใหม่ ไม่งั้น field ที่แก้ถูกแล้วยังค้างขีดแดงเดิมอยู่
    clearServerErrors() {
      Object.keys(this.errors).forEach(key => {
        this.errors[key] = [];
      });
    },
    handleSaveError(err) {
      const errors = err?.response?.data?.errors;

      // ไม่ใช่ error ราย field (500 / คีย์ไม่ผ่าน / ลายเซ็นไม่ตรง / เน็ตหลุด) — ไม่มีช่องไหนให้ชี้
      // และ message จาก server เป็นข้อความดิบของ exception จึงไม่เอามาโชว์ ใช้ข้อความกลาง ๆ แทน
      if (!errors) {
        console.error(err);
        this.$toast.error("<B>" + this.$t('Failed') + "</B>\n" + this.$t('Unable to save. Please try again.'));
        return;
      }

      this.oldParams = _.clone(this.form);
      Object.keys(this.errors).forEach(key => {
        this.errors[key] = errors[key] || [];
      });

      // ฟอร์มยาวเกินหนึ่งหน้าจอ — เลื่อนไปหา field แรกที่ผิดเอง ผู้ใช้จะได้ไม่ต้องไล่หา
      this.$nextTick(() => {
        const invalid = this.$refs.offcanvas?.querySelector('.is-invalid');
        if (invalid) invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
    async handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      this.clearServerErrors();

      await Customers.create(this.setParamsBeforeSend()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Customer Success!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        this.handleSaveError(err);
        this.loadingSave = false;
      });
    },
    async handleUpdate() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      this.clearServerErrors();

      await Customers.update(this.customerId, this.setParamsBeforeSend()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Customer Success!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        this.handleSaveError(err);
        this.loadingSave = false;
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas',async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
        console.error(err);
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  },
  computed: {
    addresses() {
      return searchAddressByZipcode(this.form.postal_code, 100);
    },
    districts() {
      if (!this.addresses.find((addresse) => addresse.district == this.form.sub_district)) {
        this.form.sub_district ='0';
      }
      return [
        {
          id: '0',
          text: this.$t('Select')+'...',
          module: 'sub_district'
        }
      ].concat([...new Set(this.addresses.map(item => item.district))].map(item => { return { id: item, text: item, module: 'sub_district' } }));
    },
    amphoes() {
      if (!this.addresses.find((addresse) => addresse.amphoe == this.form.district)) {
        this.form.district ='0';
      }
      return [
        {
          id: '0',
          text: this.$t('Select')+'...',
          module: 'district'
        }
      ].concat([...new Set(this.addresses.map(item => item.amphoe))].map(item => { return { id: item, text: item, module: 'district' } }));
    },
    provinces() {
      if (!this.addresses.find((addresse) => addresse.province == this.form.province)) {
        this.form.province ='0';
      }
      return [{ id: '0', text: this.$t('Select')+'...', module: 'province' }].concat([...new Set(this.addresses.map(item => item.province))].map(item => { return { id: item, text: item, module: 'province' } }));
    },
  }
}
</script>
